import { computed, observable } from 'mobx';
import { SuperscriptScope } from '.';
import { FootnoteOptions } from '../models/configuration/disclaimers/FootnoteOptions';

export interface DisclaimerMeta {
    type: string;
    text: string;
    order?: number;
}

export interface SuperscriptMeta {
    id?: string;
    superscript: number | string;
    type: string;
    text: string;
    order: number;
}

/**
 * This class helps manage what what superscripts are associated to what disclaimer types.
 *
 * Keep in mind if you need to manage more than one scope of superscripts, (eg. Module vs Fund Card),
 * then a new instance of this class is required for each scope
 *
 * @export
 * @class DisclaimerSuperscriptsStore
 */
export class DisclaimerSuperscriptsStore {
    private static KEY_SEPARATOR = '-|-';

    /**
     * the default options to use for superscript display
     *
     * @protected
     */
    private static DEFAULT_OPTIONS: FootnoteOptions = { type: 'numeric' };

    /**
     * When symbols are used for superscripts and the provided list runs out, we need to apply fallback logic based on the number run outs.
     */
    public fallbackCount: number = 0;

    /**
     * Defines he current index for working out the next superscript to be associated with a disclaimer
     */
    public superscriptIndex: number = 0;

    /**
     * Superscript Scope helps define the unique ids for superscript anchors
     */
    @observable.ref
    public superscriptScope: SuperscriptScope;

    /**
     * disclaimerMetaList is the configuration provided to help setup the superscripts
     *
     * @protected
     */
    @observable.ref
    protected disclaimerMetaList: Array<DisclaimerMeta>;

    /**
     * disclaimerSuperscripts is a mapping of the disclaimer types as keys to the superscript number
     *
     * @protected
     */
    protected disclaimerSuperscripts: Map<string, SuperscriptMeta> = new Map();

    /**
     * options defines how the superscripts display, eg. symbol or number
     *
     * @protected
     */
    @observable.ref
    private superscriptOptions: FootnoteOptions;

    /**
     *
     * @private
     */
    private parentStore: DisclaimerSuperscriptsStore;

    /**
     * Identifies the number of superscripts available
     *
     * @readonly
     */
    public get size() {
        return this.disclaimerSuperscripts.size;
    }

    constructor(
        disclaimerMetaList: Array<DisclaimerMeta>,
        scope: SuperscriptScope,
        options?: FootnoteOptions,
        parentStore?: DisclaimerSuperscriptsStore
    ) {
        this.disclaimerMetaList = disclaimerMetaList;
        this.superscriptScope = scope;
        // As it is possible for the superscripts to be shared across scopes, we then use the parent store to drive child stores
        if (parentStore) {
            this.parentStore = parentStore;
            this.superscriptOptions = parentStore.options;
            this.superscriptIndex = parentStore.superscriptIndex;
            this.fallbackCount = parentStore.fallbackCount;
        } else {
            this.superscriptOptions = options;
        }
        this.loadSuperscripts();
    }

    /**
     * Get the superscript associated with the disclaimer type & text
     *
     * @param type
     * @param text
     * @returns number
     */
    public get(type: string, text: string): SuperscriptMeta {
        if (this.parentStore && this.parentStore.has(type, text)) {
            const parentSuperscriptMeta = this.parentStore.get(type, text);
            return {
                ...parentSuperscriptMeta,
                id: this.getSuperscriptId(parentSuperscriptMeta.superscript),
            };
        }

        const key = this.getKey({ type, text });
        if (this.disclaimerSuperscripts.has(key)) {
            const meta = this.disclaimerSuperscripts.get(key);
            return meta;
        }
        return;
    }

    /**
     * Checks to see if the disclaimer type and text have an associated superscript
     *
     * @param type
     * @returns
     */
    public has(type: string, text: string) {
        const key = this.getKey({ type, text });
        const foundInParent =
            this.parentStore &&
            this.parentStore.disclaimerSuperscripts.has(key);
        const foundInSelf = this.disclaimerSuperscripts.has(key);
        return foundInParent || foundInSelf;
    }

    /**
     * Loop over the superscript map executing the provided callback,
     * note the first parameter in the callback references the superscript meta {superscript, type, text}
     *
     * Additionally, we are using this.filteredAndSortedDisclaimersMetaList & this.get to retrieve all disclaimers from the parent & current instance
     *
     * @param callbackfn
     * @param [thisArg]
     * @returns
     */
    public forEach(
        callbackfn: (meta: SuperscriptMeta, disclaimerKey: string) => void,
        thisArg?: any
    ): void {
        this.filteredAndSortedDisclaimerMetaList.forEach(disclaimerMeta => {
            const { type, text } = disclaimerMeta;
            const superscriptMeta = this.get(type, text);
            if (superscriptMeta) {
                const disclaimerKey = this.getKey(disclaimerMeta);
                callbackfn(superscriptMeta, disclaimerKey);
            }
        }, thisArg);
    }

    /**
     * get the provided or default superscript options
     *
     * @protected
     * @returns
     */
    @computed
    public get options(): FootnoteOptions {
        return (
            this.superscriptOptions ||
            DisclaimerSuperscriptsStore.DEFAULT_OPTIONS
        );
    }

    /**
     * get the symbol fallback type, defaulting to numeric if not found
     *
     * @readonly
     * @protected
     */
    @computed
    private get symbolFallbackType() {
        if (this.options.type === 'symbol') {
            return this.options.fallback || 'numeric';
        }
        return;
    }

    /**
     * Reorder disclaimer types based on optional order field, pushing unset items to the back.
     *
     * If no order has been defined, then ordering will not be applied.
     *
     * @returns void
     */
    @computed
    private get sortedDisclaimerMetaList(): Array<DisclaimerMeta> {
        const sorted = [].concat(...this.disclaimerMetaList);
        if (sorted.some(disclaimer => disclaimer.order !== undefined)) {
            let maxOrder = sorted.reduce((max, disclaimer) => {
                if (disclaimer.order && disclaimer.order > max) {
                    max = disclaimer.order;
                }
                return max;
            }, 0);
            maxOrder += 1;
            sorted.sort((a, b) => {
                const aOrder = a.order || maxOrder;
                const bOrder = b.order || maxOrder;
                return aOrder - bOrder;
            });
        }
        return sorted;
    }

    @computed
    private get filteredAndSortedDisclaimerMetaList(): Array<DisclaimerMeta> {
        const keys = new Set();
        return this.sortedDisclaimerMetaList.reduce((list, meta) => {
            const disclaimerKey = this.getKey(meta);
            if (!keys.has(disclaimerKey)) {
                keys.add(disclaimerKey);
                list.push(meta);
            }
            return list;
        }, []);
    }

    /**
     * Setup a key for identifying unique disclaimer types,
     * and as we can have the same type but different text between entities,
     * the disclaimer text forms part of the key.
     *
     * @private
     * @param disclaimerMeta
     * @returns string
     */
    private getKey(
        disclaimerMeta: Pick<DisclaimerMeta, 'type' | 'text'>
    ): string {
        const { type, text } = disclaimerMeta;
        const parts = [type, text].reduce((items, item) => {
            if (!!item) {
                items.push(item);
            }
            return items;
        }, []);
        return parts.join(DisclaimerSuperscriptsStore.KEY_SEPARATOR);
    }

    private getSuperscriptForIndex(currentIndex: number): number | string {
        if (this.options.type === 'numeric') {
            return currentIndex + 1;
        } else if (this.options.type === 'symbol') {
            if (this.fallbackCount > 0) {
                if (this.symbolFallbackType === 'symbolIncrement') {
                    const superscriptSymbols = [];
                    for (let i = 0; i <= this.fallbackCount; i++) {
                        superscriptSymbols.push(
                            this.options.symbols[currentIndex]
                        );
                    }
                    return superscriptSymbols.join('');
                } else {
                    return currentIndex + 1 - this.options.symbols.length;
                }
            }
            return this.options.symbols[currentIndex];
        }
        return;
    }

    private getNextSuperscriptIndex(currentIndex: number): number {
        if (this.options.type === 'symbol') {
            const lastIndex = this.options.symbols?.length - 1 || 0;
            if (lastIndex <= currentIndex) {
                this.fallbackCount += 1;
                if (this.symbolFallbackType === 'symbolIncrement') {
                    return 0;
                }
            }
        }
        return currentIndex + 1;
    }

    /**
     * Define the superscript id to be used with the anchor reference and footnote id
     *
     * @private
     * @param superscript
     * @returns
     */
    private getSuperscriptId(superscript: number | string): string {
        if (this.options.enableAnchors === true) {
            return [this.superscriptScope, superscript].join('-');
        }
        return;
    }

    /**
     * Setup disclaimerKey to superscript mapping based on current order,
     * skipping potential duplicate keys
     *
     * @returns void
     */
    private loadSuperscripts() {
        let orderIndex = 0;
        this.filteredAndSortedDisclaimerMetaList.forEach(disclaimer => {
            if (!this.has(disclaimer.type, disclaimer.text)) {
                const superscript = this.getSuperscriptForIndex(
                    this.superscriptIndex
                );
                if (superscript) {
                    const disclaimerKey = this.getKey(disclaimer);
                    this.disclaimerSuperscripts.set(disclaimerKey, {
                        id: this.getSuperscriptId(superscript),
                        superscript,
                        type: disclaimer.type,
                        text: disclaimer.text,
                        order: orderIndex,
                    });
                    orderIndex++;
                    this.superscriptIndex = this.getNextSuperscriptIndex(
                        this.superscriptIndex
                    );
                }
            }
        });
    }
}



// WEBPACK FOOTER //
// ./src/stores/DisclaimerSuperscriptsStore.ts